<template>
    <modals-generic
    :headerColor="detailInfo.headerStyles.color"
    :title="detailInfo.typeDescription"
    :subTitle="detailInfo.subTitle"
    :bigIcon="detailInfo.headerStyles.bigIcon"
    :buttonsTopData="detailInfo.operationHasReceipt ? buttonsTopData : null"
    :showFooter="!showLoader && !applyErrorState && showFooter"
    :buttonsFooterData="buttonsFooterData"
    :triggerClose="triggerClose"
    sizeClass="big"
    noPaddingBottom
    @hideMe="[emit('hideMe'), cancelAllPendingRequests()]">
        <div v-if="!showLoader && !applyErrorState" class="operator-details" :class="{ mobile: isMobile }">
            <div class="info-blocks" :class="{ 'group-wrapper': !isMobile }">
                <!-- Status Block -->
                <detail-status-block
                :operatorDetails="operatorDetails"
                :operationTypeId="detailInfo.operationTypeId"
                :isMobile="isMobile" />
                 <!-- Editions Block -->
                <detail-editions-block
                v-if="operatorDetails.condition && detailInfo.operationTypeId !== 501"
                :operatorDetails="operatorDetails"
                :accounts="getAccountsList"
                :operationTypesList="getOperationsTypesList"/>
                <!-- Detail Data Block -->
                <detail-data-block :operatorDetails="operatorDetails" :isMobile="isMobile" />
                <!-- Operator Accounts Divider -->
                <div class="operator-accounts-container" :class="{ 'group-wrapper': isMobile }">
                    <divider-generic
                    icon="lock"
                    :applyBorder="!isMobile"
                    :title="t('approvals.signed.modal.details.operatorDetails.accountsTitle')"/>
                    <!-- Account Accessed Block -->
                    <account-accessed-block
                    class="accounts-block"
                    v-for="account in operatorDetails.accountAccessConfig"
                    :key="account.account.id"
                    :account="account"
                    :defaultAccount="operatorDetails.defaultAccountId"
                    :isMobile="isMobile"/>
                </div>
            </div>
            <!-- Operation Signatures -->
            <div class="signatures-container" :class="{ mobile: isMobile }">
                <!-- Signatures Divider -->
                <divider-generic
                icon="document-edit"
                :title="t('approvals.signed.modal.details.signatures.title')"
                :applyBorder="!isMobile" />
                <!-- Signatures Chart -->
                <div class="percentage-chart-block">
                    <div class="chart-container">
                        <!-- Chart -->
                        <chart-simple :graphData="detailInfo.graphData" :colorsArray="getAppThemeMode === 'light' ? ['#00894E', '#DDDDDE'] : ['#5E977E', '#4A4A4A']"/>
                    </div>
                    <!-- Signed Percentage -->
                    <div v-if="detailInfo.signedPercentage !== 0" class="signed-percentage">
                        <span class="percentage">{{ detailInfo.signedPercentage }}%</span>
                        <div class="label">{{ t('approvals.signed.modal.details.signatures.signed') }}</div>
                    </div>
                    <!-- Not Signed Percentage -->
                    <div v-if="detailInfo.notSignedPercentage !== 0" class="not-signed-percentage">
                        <span class="percentage">{{ detailInfo.notSignedPercentage }}%</span>
                        <div class="label">{{ t('approvals.signed.modal.details.signatures.notSigned') }}</div>
                    </div>
                </div>
                <!-- Signatures Table -->
                <signatures-table :signatureList="detailInfo.signatureList" :isMobile="isMobile" />
            </div>
        </div>
        <!-- Detail Error State -->
        <empty-state-error v-if="applyErrorState && !showLoader" :message="t('approvals.operators.operatorDetails.movementDetailError')"/>
        <!-- Feedback Toast -->
        <toast-generic
        v-if="showFeedbackToast"
        :toastType="feedbackToastData.toastType"
        :feedbackText="feedbackToastData.text"
        :buttonsParams="feedbackToastData.buttons"
        @hideMe="showFeedbackToast = false"/>
        <!-- Loader Generic -->
        <loaders-generic
        v-if="showLoader" fullSize/>
    </modals-generic>
</template>

<script setup>
import ModalsGeneric from '@/components/generic/modals/side/Generic'
import EmptyStateError from '@/components/generic/emptyStates/Error'
import ToastGeneric from '@/components/generic/toasts/Generic'
import LoadersGeneric from '@/components/generic/loaders/Generic'
import DetailStatusBlock from '@/components/features/approvals/details/operatorDetails/DetailStatusBlock'
import DetailDataBlock from '@/components/features/approvals/details/operatorDetails/DetailDataBlock'
import DividerGeneric from '@/components/generic/dividers/Generic'
import AccountAccessedBlock from '@/components/features/approvals/details/operatorDetails/AccountAccessedBlock'
import ChartSimple from '@/components/generic/charts/Simple'
import SignaturesTable from '@/components/features/approvals/details/SignaturesTable'
import DetailEditionsBlock from '@/components/features/approvals/operators/details/DetailEditionsBlock'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'

/* Aux */
import { cancelAllPendingRequests } from '@/store/helpers/accessControl'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Vue */
import { ref, computed } from 'vue'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/** Props
@detailsInfo {Object} - info for the details modal
@payloadDetailModal {Object} - object with payload for the details request
@requesAction {String} - details request action
@buttonsTopData {Object} - data for the top buttons
@showFooter {Boolean} - prop to control display of footer
@buttonsFooterData {Array} - data for the footer buttons
@triggerClose {Boolean} - prop to trigger close of filter modal so animation happens before our modal disapears
@isMobile {Boolean}
*/
const props = defineProps({
    detailInfo: Object,
    payloadDetailModal: Object,
    requestAction: String,
    buttonsTopData: Object,
    showFooter: Boolean,
    buttonsFooterData: Array,
    triggerClose: Boolean,
    isMobile: Boolean
})

const emit = defineEmits(['hideMe'])

/* mapActions & mapGetters */
const {
    requestAccountsList,
    requestProfilesList,
    requestOperatorDetail,
    requestOperationsTypes
} = mapActions(moduleMapper.APPROVALS)
const {
    getOperationsTypesList,
    getAccountsList,
    getProfilesList,
    getOperatorDetails
} = mapGetters(moduleMapper.APPROVALS)
const { getAppThemeMode } = mapGetters()

/* Show Data */
const showLoader = ref(false)

/*************************************************
*                                                *
*                 Requests Logic                 *
*                                                *
*************************************************/
const requestDetails = async () => {
	showLoader.value = true

	try {
        await requestOperationsTypes({ isSigned: true, onlyListed: true })
        await requestAccountsList()

        if (props.detailInfo.operationTypeId === 501) {
            await requestProfilesList()
            setOperationType501Details()
        } else {
            await requestOperatorDetail(props.detailInfo.operatorId)
            operatorDetails.value = {
                ...getOperatorDetails.value,
                accountAccessConfig: accountAccessConfigHandler(getOperatorDetails.value.accountAccessConfig)
            }
        }

        showLoader.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
		showLoader.value = false
		applyErrorState.value = true
		errorHandler(error.response.data, requestDetails)
	}
}

/*************************************************
 *                                                *
 *             Success/Error Handlers             *
 *                                                *
 *************************************************/
const showFeedbackToast = ref(false)
const applyErrorState = ref(false)

const feedbackToastData = ref(emptyObjectConstructor())

const errorHandler = (data, retryFn) => {
	feedbackToastData.value = errorObjectConstructor(data, retryFn)
	showFeedbackToast.value = true
}

/*************************************************
*                                                *
*                Operator Details                *
*                                                *
*************************************************/
const operatorDetails = ref({})

const operatorStatus = computed(() => {
    switch (props.detailInfo.status.id) {
        case 1:
        case 3:
            return { id: 3 }
        case 7:
        case 10:
        case 11:
            return { id: 2, condition: t('approvals.signed.modal.details.operatorDetails.condition.pendingCreation') }
        case 5:
        case 8:
        case 9:
            return { id: 4 }
        default:
            return null
    }
})

const operatorProfileDescription = computed(() => {
    return getProfilesList.value.find(profile => profile.id === props.detailInfo.profileId).operatorProfileDescription
})

const accountAccessConfigHandler = (accountAccessConfig) => {
    const obj = {}

    Object.keys(accountAccessConfig).forEach(key => {
        obj[key] = {
            operationLimits: operationLimits(accountAccessConfig[key].operationLimits),
            hasAccess: accountAccessConfig[key].hasAccess,
            account: { ...getAccountsList.value.bankAccountView.find(account => account.id === Number(key)) }
        }
    })

    return obj
}

const operationLimits = (operationLimits) => {
    const obj = {}

    if (operationLimits) {
        Object.keys(operationLimits).forEach(key => {
            obj[key] = {
                dailyPlafond: operationLimits[key].dailyPlafond,
                operationType: {
                    ...getOperationsTypesList.value.find(type => type.id === Number(key)),
                    name: getOperationsTypesList.value.find(type => type.id === Number(key)).type
                },
                transactionLimit: operationLimits[key].transactionLimit
            }
        })

        return obj
    } else {
        return {}
    }
}

/* Details for operation type 501 (Criar Operador) */
const setOperationType501Details = () => {
    operatorDetails.value = {
        status: {
            operatorStatusDescription: t('approvals.signed.modal.details.operatorDetails.status.creation'),
            operatorStatusValue: operatorStatus.value.id
        },
        condition: operatorStatus.value.condition,
        createdBy: { name: props.detailInfo.initiatorContractName },
        lastUpdate: props.detailInfo.endDate,
        channel: { name: props.detailInfo.channel },
        name: props.detailInfo.operatorName,
        email: props.detailInfo.email,
        msisdn: props.detailInfo.msisdn,
        nif: props.detailInfo.nif,
        accessToNewAccounts: props.detailInfo.accessToNewAccounts,
        profile: { operatorProfileDescription: operatorProfileDescription.value },
        weight: props.detailInfo.weight,
        accountAccessConfig: accountAccessConfigHandler(props.detailInfo.accountAccessConfig),
        defaultAccountId: props.detailInfo.defaultAccountId
    }
}

requestDetails()
</script>

<style lang="scss" scoped>
.operator-details {
    .info-blocks {
        .operator-accounts-container {
            display: flex;
            flex-direction: column;
            gap: 24px;
        }
    }

    .signatures-container {
        display: flex;
        flex-direction: column;

        gap: 24px;
        .percentage-chart-block {
            display: flex;
            flex-direction: row;
            align-items: center;
            gap: 24px;

            height: 88px;

            margin-bottom: 10px;

            .chart-container {
                width: 88px;
                height: 88px;
                position: relative;
            }

            .signed-percentage,
            .not-signed-percentage {
                display: flex;
                flex-direction: column;
                gap: 10px;

                .percentage {
                    @include H2-Medium;
                    color: var(--secondary);
                }

                .label {
                    @include H7-Regular;
                    color: var(--secondary-dark-1);
                }
            }

            .signed-percentage {
                .percentage {
                    color: var(--positive);
                }
            }
        }

        &.mobile {
            border-radius: 12px;

            background-color: var(--white);
            box-shadow: 0 10px 16px 0 rgba(0, 50, 90, 0.08);

            :deep(.divider-generic-wrapper) {
                margin-left: 16px;
            }

            .percentage-chart-block {
                .chart-container {
                    margin-left: 16px;
                }
            }
        }
    }
}
</style>
